import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { fn } from '../api/functions';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useUserAccess } from '../hooks/useUserAccess';
import { UserIcon, CheckIcon, ShoppingCartIcon } from '@heroicons/react/24/outline';

export const UserProfilePage = () => {
  const { currentUser } = useAuth();
  const { userAccess, loading: accessLoading, canCreateStory, remainingTokens, hasAccess } = useUserAccess();
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState({
    displayName: '',
    birthYear: '',
    hometown: '',
    currentLocation: '',
    occupation: '',
    interests: '',
    familyInfo: '',
    bio: '',
    isPublic: false,
    preferredLanguage: 'en'
  });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!currentUser) return;

    // Load user profile
    const loadProfile = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setProfileData({
            displayName: data.displayName || currentUser.displayName || '',
            birthYear: data.birthYear || '',
            hometown: data.hometown || '',
            currentLocation: data.currentLocation || '',
            occupation: data.occupation || '',
            interests: data.interests || '',
            familyInfo: data.familyInfo || '',
            bio: data.bio || '',
            isPublic: data.isPublic || false,
            preferredLanguage: data.preferredLanguage || 'en'
          });
        } else {
          // Initialize with basic info from Firebase Auth
          setProfileData(prev => ({
            ...prev,
            displayName: currentUser.displayName || ''
          }));
        }
      } catch (error) {
        console.error('Error loading profile:', error);
      }
      setLoading(false);
    };

    // Load user stats
    const loadStats = async () => {
      try {
        const result = await fn.getUserStats();
        if (result.data.success) {
          setStats(result.data.stats);
        }
      } catch (error) {
        console.error('Error loading stats:', error);
      }
    };

    loadProfile();
    loadStats();
  }, [currentUser]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      const result = await fn.updateUserProfile({ profileData });
      if (result.data.success) {
        setMessage('Profile updated successfully!');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Your Profile</h1>
        <p className="text-gray-600 mt-2">
          This information helps us generate better, more personalized questions for your stories.
        </p>
      </div>

      {/* Credit Status Section */}
      {!accessLoading && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 mb-8 border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Story Creation Credits</h3>
              {hasAccess ? (
                <>
                  <p className="text-gray-700">
                    You have <span className="font-bold text-blue-600">{remainingTokens}</span> story credit{remainingTokens !== 1 ? 's' : ''} remaining
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Used {userAccess?.tokensUsed || 0} of {userAccess?.storyTokens || 0} credits from your Memory Stream purchase
                  </p>
                  {userAccess?.orderId && (
                    <p className="text-xs text-gray-400 mt-2">
                      Order ID: {userAccess.orderId}
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p className="text-gray-700">You don't have any story credits yet</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Purchase a Memory Stream package to start creating stories
                  </p>
                </>
              )}
            </div>
            <div className="text-right">
              {hasAccess ? (
                <div className="text-3xl font-bold text-blue-600">{remainingTokens}</div>
              ) : (
                <button 
                  onClick={() => navigate('/story/new')}
                  className="memory-stream-button flex items-center"
                >
                  <ShoppingCartIcon className="h-5 w-5 mr-2" />
                  Purchase Now
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Profile Form */}
        <div className="lg:col-span-2">
          <div className="memory-stream-card">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 mb-2">
                      Display Name
                    </label>
                    <input
                      type="text"
                      id="displayName"
                      name="displayName"
                      value={profileData.displayName}
                      onChange={handleInputChange}
                      placeholder="Your full name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="birthYear" className="block text-sm font-medium text-gray-700 mb-2">
                      Birth Year
                    </label>
                    <input
                      type="number"
                      id="birthYear"
                      name="birthYear"
                      value={profileData.birthYear}
                      onChange={handleInputChange}
                      placeholder="e.g., 1975"
                      min="1900"
                      max={new Date().getFullYear()}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Helps generate era-specific questions</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="hometown" className="block text-sm font-medium text-gray-700 mb-2">
                      Hometown
                    </label>
                    <input
                      type="text"
                      id="hometown"
                      name="hometown"
                      value={profileData.hometown}
                      onChange={handleInputChange}
                      placeholder="Where you grew up"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="currentLocation" className="block text-sm font-medium text-gray-700 mb-2">
                      Current Location
                    </label>
                    <input
                      type="text"
                      id="currentLocation"
                      name="currentLocation"
                      value={profileData.currentLocation}
                      onChange={handleInputChange}
                      placeholder="Where you live now"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="occupation" className="block text-sm font-medium text-gray-700 mb-2">
                    Occupation/Career
                  </label>
                  <input
                    type="text"
                    id="occupation"
                    name="occupation"
                    value={profileData.occupation}
                    onChange={handleInputChange}
                    placeholder="Current or main occupation"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Personal Details */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Details</h3>
                
                <div>
                  <label htmlFor="interests" className="block text-sm font-medium text-gray-700 mb-2">
                    Interests & Hobbies
                  </label>
                  <textarea
                    id="interests"
                    name="interests"
                    value={profileData.interests}
                    onChange={handleInputChange}
                    rows={3}
                    placeholder="What you enjoy doing, hobbies, interests..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label htmlFor="familyInfo" className="block text-sm font-medium text-gray-700 mb-2">
                    Family Information
                  </label>
                  <textarea
                    id="familyInfo"
                    name="familyInfo"
                    value={profileData.familyInfo}
                    onChange={handleInputChange}
                    rows={3}
                    placeholder="Family members, relationships, traditions..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label htmlFor="bio" className="block text-sm font-medium text-gray-700 mb-2">
                    Short Bio
                  </label>
                  <textarea
                    id="bio"
                    name="bio"
                    value={profileData.bio}
                    onChange={handleInputChange}
                    rows={4}
                    placeholder="Tell us a bit about yourself..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Settings */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Settings</h3>
                
                <div className="space-y-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="isPublic"
                      checked={profileData.isPublic}
                      onChange={handleInputChange}
                      className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700">Make profile public</span>
                      <p className="text-xs text-gray-500">Allow others to see your profile information</p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                <div>
                  {message && (
                    <div className={`text-sm ${message.toLowerCase().includes('success') ? 'text-green-600' : 'text-red-600'}`}>
                      {message.toLowerCase().includes('success') && <CheckIcon className="inline h-4 w-4 mr-1" />}
                      {message}
                    </div>
                  )}
                </div>
                
                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={() => navigate('/')}
                    className="memory-stream-button-secondary"
                  >
                    Back to Dashboard
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="memory-stream-button"
                  >
                    {saving ? 'Saving...' : 'Save Profile'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Stats Sidebar */}
        <div className="lg:col-span-1">
          <div className="memory-stream-card">
            <div className="flex items-center mb-4">
              <UserIcon className="h-6 w-6 text-blue-600 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Your Story Stats</h3>
            </div>
            
            {stats ? (
              <div className="space-y-4">
                <div className="text-center p-4 bg-blue-50 rounded-md">
                  <div className="text-2xl font-bold text-blue-600">{stats.totalStories}</div>
                  <div className="text-sm text-blue-800">Stories Created</div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-3 bg-green-50 rounded-md">
                    <div className="text-xl font-semibold text-green-600">{stats.totalAnswers}</div>
                    <div className="text-xs text-green-800">Answers</div>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-md">
                    <div className="text-xl font-semibold text-purple-600">{stats.totalQuestions}</div>
                    <div className="text-xs text-purple-800">Questions</div>
                  </div>
                </div>
                
                <div className="text-center p-3 bg-orange-50 rounded-md">
                  <div className="text-lg font-semibold text-orange-600">{stats.overallCompletionRate}%</div>
                  <div className="text-xs text-orange-800">Overall Completion</div>
                </div>

                {stats.stories && stats.stories.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Recent Stories</h4>
                    <div className="space-y-2">
                      {stats.stories.slice(0, 3).map(story => (
                        <div key={story.id} className="p-2 bg-gray-50 rounded text-xs">
                          <div className="font-medium text-gray-900 truncate">{story.title}</div>
                          <div className="text-gray-600">{story.answerCount}/{story.questionCount} answers</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-sm text-gray-500 mt-2">Loading stats...</p>
              </div>
            )}
          </div>

          <div className="memory-stream-card mt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">💡 Profile Tips</h3>
            <div className="space-y-3 text-sm text-gray-600">
              <p>• Adding your birth year helps generate era-specific questions about historical events you experienced.</p>
              <p>• Your hometown information creates questions about local culture and regional experiences.</p>
              <p>• Career details help balance work stories with personal memories.</p>
              <p>• The more complete your profile, the more personalized your story questions become!</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
